import { useCallback, useRef, useState } from "react";
import LogRocket from "logrocket";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../contexts/AuthContext";
import {
  BillingError,
  createCheckoutSession,
  getBillingErrorMessage,
  redirectToBillingUrl,
} from "../utils/billingClient";

interface BillingCheckout {
  checkoutPending: boolean;
  checkoutError: string | null;
  checkoutResolution:
    | "activation_pending"
    | "review_required"
    | "migration_required"
    | null;
  startCheckout: () => Promise<void>;
}

export const useBillingCheckout = (): BillingCheckout => {
  const { currentUser, signInWithGoogle } = useAuth();
  const [checkoutPending, setCheckoutPending] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [checkoutResolution, setCheckoutResolution] = useState<
    "activation_pending" | "review_required" | "migration_required" | null
  >(null);
  const pendingRef = useRef(false);
  const attemptIdRef = useRef<string | null>(null);

  const startCheckout = useCallback(async () => {
    if (pendingRef.current) return;

    pendingRef.current = true;
    setCheckoutPending(true);
    setCheckoutError(null);
    setCheckoutResolution(null);

    try {
      if (!currentUser) {
        await signInWithGoogle();
      }

      if (!attemptIdRef.current) {
        attemptIdRef.current = uuidv4();
      }

      const session = await createCheckoutSession(attemptIdRef.current);
      LogRocket.log("Stripe checkout redirect", {
        requestId: session.requestId,
      });
      redirectToBillingUrl(session.url);
    } catch (error) {
      if (error instanceof BillingError && !error.retryable) {
        attemptIdRef.current = null;
      }
      if (error instanceof BillingError) {
        if (error.code === "ALREADY_SUBSCRIBED") {
          setCheckoutResolution("activation_pending");
        } else if (error.code === "BILLING_REVIEW_REQUIRED") {
          setCheckoutResolution("review_required");
        } else if (error.code === "CUSTOMER_OWNERSHIP_MISMATCH") {
          setCheckoutResolution("migration_required");
        }
      }

      const normalizedError =
        error instanceof Error ? error : new Error("Unknown billing error");
      LogRocket.captureException(normalizedError, {
        extra: {
          billingCode:
            error instanceof BillingError ? error.code : "UNKNOWN_ERROR",
          requestId:
            error instanceof BillingError && error.requestId
              ? error.requestId
              : "",
        },
      });
      setCheckoutError(getBillingErrorMessage(error));
    } finally {
      pendingRef.current = false;
      setCheckoutPending(false);
    }
  }, [currentUser, signInWithGoogle]);

  return {
    checkoutPending,
    checkoutError,
    checkoutResolution,
    startCheckout,
  };
};
